import { Fix64 } from "../maths/fix64";
import { Cell, manhattan } from "../world/cell";
import { distanceSquared } from "../world/roadGeometry";
import { Command, Commands } from "./command";
import { CardKind, Tower } from "./entities";
import { LevelPhase, LevelSim } from "./levelSim";
import { Offer } from "./offer";
import { wyrmChoiceCommand } from "./wyrm";
import { canEngagePet } from "./wildPet";

/**
 * Deterministic heuristic player: takes tower cards while under-towered (preferring damage dealers), otherwise path
 * cards whenever they are offered, placed to keep the road long; towers go where they cover the most road;
 * upgrades what is left over; calls stages early. Used for goldens, smoke tests, the headless level check and the
 * capture footage, so it has to play a level the way a person would rather than stall halfway through.
 */
export class ScriptedPlayer {
  callStagesEarly = true;
  /** M40: retries this player pays for when the Starfall Wyrm flies off; 0 (the goldens) = let it go. */
  wyrmRetries = 0;
  /** Pets v2: chase the wild pet when it shows (its golden); false (every other golden) = let it run off. */
  engageWildPet = false;

  /** Returns the next command, or null to advance one tick. */
  next(sim: LevelSim): Command | null {
    if (sim.isOver) return null;
    const wyrm = wyrmChoiceCommand(sim, this.wyrmRetries);
    if (wyrm !== null) { if (this.wyrmRetries > 0) this.wyrmRetries--; return wyrm; }
    if (this.engageWildPet && canEngagePet(sim)) return Commands.engagePet();
    const offer = sim.currentOffer;
    if (offer !== null) {
      if (!offer.hasPick) return Commands.pickCard(chooseCard(sim, offer));
      if (offer.pickedCard.kind === CardKind.Path) {
        // No legal placement (the map around the road end is full). Skipping is the only valid move; the
        // old code called placeSegment(0) on an empty ghost list and issued a command that could not be executed.
        return offer.ghosts.length === 0 ? Commands.skipOffer() : Commands.placeSegment(chooseGhost(sim, offer));
      }
      if (offer.pickedCard.kind === CardKind.Upgrade) {
        const target = chooseUpgrade(sim);
        return target === null ? Commands.skipOffer() : Commands.upgradeTower(target.id);
      }
      // Tier 4: the branch alternates by type and level, so the goldens cover both of every pair over the campaign.
      if (offer.pickedCard.kind === CardKind.Ascend) return Commands.chooseBranch((offer.pickedCard.index + sim.levelNumber) % 2);
      if (offer.pickedCard.kind === CardKind.Skill) return Commands.learnSkill();
      const cell = chooseTowerCell(sim, offer.pickedCard.index);
      return cell === null ? Commands.skipOffer() : Commands.placeTower(cell.x, cell.y);
    }
    if (sim.phase === LevelPhase.Ready) return Commands.startWaves();
    if (sim.phase === LevelPhase.BetweenWaves && this.callStagesEarly) return Commands.callNextStage();
    return null;
  }
}

function chooseCard(sim: LevelSim, offer: Offer): number {
  let towers = 0;
  for (const t of sim.towerSlots) if (t.alive && !sim.balance.towers[t.archetype].isBase) towers++;
  // Keep adding towers as the stages get harder, rather than stopping at a fixed handful.
  const wantTower = towers < 3 + sim.stagesCleared;
  // Road whenever it is offered: the road pace (§4.4) already spaces road cards out as the road grows. An older rule —
  // upgrades beat road once the road passed nine tiles — left the track stubby and most of the map unused, which made
  // this class useless as a check on whether a hand-drawn level has room for a long road. Upgrades go on the offers
  // that carry no road.
  const wantRoad = offer.cards.some((c) => c.kind === CardKind.Path);
  // Tier 4: an Ascend card whenever one comes (it lifts a whole type), a skill card when no tower is wanted.
  const ascend = offer.cards.findIndex((c) => c.kind === CardKind.Ascend);
  if (ascend >= 0) return ascend;
  const skillCard = offer.cards.findIndex((c) => c.kind === CardKind.Skill);
  if (!wantTower && skillCard >= 0) return skillCard;
  if (!wantTower && !wantRoad) {
    const up = offer.cards.findIndex((c) => c.kind === CardKind.Upgrade);
    if (up >= 0 && chooseUpgrade(sim) !== null) return up;
  }
  const preferred = wantTower || !wantRoad ? CardKind.Tower : CardKind.Path;
  let fallback = -1;
  for (let i = 0; i < offer.cards.length; i++) {
    const c = offer.cards[i];
    if (c.kind !== preferred) continue;
    if (c.kind === CardKind.Tower && sim.balance.towers[c.index].damage.isZero()) {
      if (fallback < 0) fallback = i;
      continue;
    }
    return i;
  }
  if (fallback >= 0) return fallback;
  for (let i = 0; i < offer.cards.length; i++) {
    if (offer.cards[i].kind === CardKind.Tower && sim.balance.towers[offer.cards[i].index].damage.isPositive()) return i;
  }
  // M55: never the Move card (this player does not move towers); a hand always holds another real card beside it.
  const other = offer.cards.findIndex((c) => c.kind !== CardKind.Move);
  return other >= 0 ? other : 0;
}

function chooseGhost(sim: LevelSim, offer: Offer): number {
  let best = 0, bestScore = -Infinity;
  for (let i = 0; i < offer.ghosts.length; i++) {
    const cells = offer.ghosts[i].cells;
    const score = manhattan(cells[cells.length - 1], sim.grid.base);
    if (score > bestScore) { bestScore = score; best = i; }
  }
  return best;
}

function chooseTowerCell(sim: LevelSim, archetype: number): Cell | null {
  const a = sim.balance.towers[archetype];
  const r2 = a.range.mul(a.range);
  const cells = sim.collectValidTowerCells();
  if (cells.length === 0) return null;                      // nowhere legal to build: skip rather than fail
  let best = cells[0], bestScore = -1;
  for (const c of cells) {
    let score = 0;
    const cx = Fix64.fromInt(c.x), cy = Fix64.fromInt(c.y);
    for (const road of sim.road.roadCells) {
      if (distanceSquared(cx, cy, Fix64.fromInt(road.x), Fix64.fromInt(road.y)).le(r2)) score++;
    }
    if (score > bestScore) { bestScore = score; best = c; }
  }
  return best;
}

/** Upgrade the upgradable tower with the most kills (the base counts too). */
function chooseUpgrade(sim: LevelSim): Tower | null {
  let best: Tower | null = null;
  for (const t of sim.towerSlots) {
    if (!sim.canUpgrade(t)) continue;
    if (best === null || t.kills > best.kills) best = t;
  }
  return best;
}
